import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, CheckCircle, ListChecks, Tray, Wrench, SealCheck, Lock, CalendarBlank, CaretRight } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { istToday } from "../lib/day";

const todayStr = () => istToday(); // IST, not UTC — see lib/day.js

export default function DailyReport() {
  const [day, setDay] = useState(todayStr());
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const isToday = day === todayStr();
  const pretty = day.split("-").reverse().join("/");

  const load = useCallback(() => {
    setData(null);
    api.get(`/api/daily/report?date=${day}`).then(({ data }) => setData(data)).catch(() => setData({}));
  }, [day]);
  useEffect(() => { load(); }, [load]);

  const openTickets = (status) => {
    const q = new URLSearchParams({ status });
    if (!isToday) q.set("asOf", day);
    navigate(`/complaints?${q.toString()}`);
  };

  const tk = data?.tickets || {};
  const ticketBoxes = [
    { status: "OPEN", icon: Tray, accent: "cyan", label: "Open", value: tk.open },
    { status: "IN_PROGRESS", icon: Wrench, accent: "amber", label: "In progress", value: tk.inProgress },
    { status: "RESOLVED", icon: SealCheck, accent: "lime", label: "Resolved", value: tk.resolved },
    { status: "CLOSED", icon: Lock, accent: "muted", label: "Closed", value: tk.closed },
  ];

  return (
    <div className="animate-fade-up space-y-6" data-testid="daily-report-screen">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[var(--fg-soft)]">End-of-day snapshot. Pick a date to review any past day; click a ticket box to view those tickets.</p>
        <label className="inline-flex items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-sm font-semibold text-[var(--fg)]">
          <CalendarBlank size={18} className="text-neon-cyan" />
          <input type="date" max={todayStr()} value={day} onChange={(e) => setDay(e.target.value)}
            className="bg-transparent text-[var(--fg)] outline-none" />
          {!isToday && <button onClick={() => setDay(todayStr())} className="ml-1 text-xs font-bold text-neon-cyan">TODAY</button>}
        </label>
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-6">
        <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-neon-lime/15 blur-3xl" />
        <Sun size={24} weight="fill" className="text-neon-lime" />
        <p className="mt-3 font-display text-2xl font-semibold text-[var(--fg)]">
          {data ? `${data.tasks?.doneToday ?? 0} tasks completed ${isToday ? "today" : "that day"}` : "Loading…"}
        </p>
        <p className="mt-1 text-sm text-[var(--fg-soft)]">{isToday ? "Keep going — small wins compound." : `Snapshot for ${pretty}.`}</p>
      </div>

      <div>
        <h2 className="mb-3 text-xs font-extrabold uppercase tracking-[0.14em] text-[var(--fg-dim)]">Tasks</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat icon={CheckCircle} accent="lime" label="Tasks done" value={data?.tasks?.doneToday} />
          <Stat icon={ListChecks} accent="amber" label="Tasks pending" value={data?.tasks?.stillPending} />
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-xs font-extrabold uppercase tracking-[0.14em] text-[var(--fg-dim)]">Tickets</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {ticketBoxes.map((b) => (
            <button key={b.status} onClick={() => openTickets(b.status)}
              className="group rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4 text-left transition hover:border-neon-cyan/50 hover:bg-white/[0.04]">
              <div className="flex items-center justify-between">
                <b.icon size={20} weight="duotone" className={accentClass(b.accent)} />
                <CaretRight size={14} className="text-[var(--fg-dim)] transition group-hover:translate-x-0.5" />
              </div>
              <p className="mt-2 font-display text-2xl font-extrabold text-[var(--fg)]">{b.value ?? "—"}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--fg-soft)]">{b.label}</p>
            </button>
          ))}
        </div>
        {tk.createdOnDay > 0 && (
          <p className="mt-3 text-xs text-[var(--fg-dim)]">{tk.createdOnDay} ticket{tk.createdOnDay === 1 ? "" : "s"} raised on {pretty}</p>
        )}
      </div>
    </div>
  );
}

function accentClass(a) {
  return a === "pink" ? "text-neon-pink" : a === "amber" ? "text-neon-amber" : a === "cyan" ? "text-neon-cyan" : a === "muted" ? "text-[var(--fg-soft)]" : "text-neon-lime";
}

function Stat({ icon: Icon, accent, label, value }) {
  return (
    <div className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4">
      <Icon size={20} weight="duotone" className={accentClass(accent)} />
      <p className="mt-2 font-display text-2xl font-extrabold text-[var(--fg)]">{value ?? "—"}</p>
      <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--fg-soft)]">{label}</p>
    </div>
  );
}
